import React, { useState, useEffect } from 'react';
import { BarChart3, Users, Clock, Download, Star } from 'lucide-react';
import { collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line
} from 'recharts';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

const AnalyticsModal = ({ show, onClose, companyId }) => {
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('7d'); // 7d, 30d, all

  useEffect(() => {
    if (show && companyId) {
      fetchAnalyticsData();
    }
  }, [show, companyId, timeRange]);

  const fetchAnalyticsData = async () => {
    setLoading(true);
    try {
      if (companyId === "demo") {
        // Generate mock data for demo
        const mockEvents = generateMockAnalyticsEvents();
        const processedData = processAnalyticsData(mockEvents);
        setAnalyticsData(processedData);
        return;
      }

      const analyticsQuery = query(
        collection(db, 'analytics'),
        where('companyId', '==', companyId),
        orderBy('timestamp', 'desc')
      );

      const querySnapshot = await getDocs(analyticsQuery);
      const events = [];
      querySnapshot.forEach((doc) => {
        events.push({ id: doc.id, ...doc.data() });
      });

      // Process analytics data
      const processedData = processAnalyticsData(events);
      setAnalyticsData(processedData);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateMockAnalyticsEvents = () => {
    const events = [];
    const mockViewers = [
      { fullName: "John Smith", email: "john.smith@example.com", deviceInfo: "Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36", viewport: { width: 1440, height: 900 } },
      { fullName: "Sarah Johnson", email: "sarah.j@example.com", deviceInfo: "Mozilla/5.0 (iPhone; CPU iPhone OS 15_0 like Mac OS X) AppleWebKit/605.1.15", viewport: { width: 390, height: 844 } },
      { fullName: "Michael Chen", email: "m.chen@example.com", deviceInfo: "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36", viewport: { width: 1920, height: 1080 } },
      { fullName: "Emma Wilson", email: "emma.w@example.com", deviceInfo: "Mozilla/5.0 (iPad; CPU OS 15_0 like Mac OS X) AppleWebKit/605.1.15", viewport: { width: 1024, height: 1366 } },
      { fullName: "David Brown", email: "d.brown@example.com", deviceInfo: "Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36", viewport: { width: 1280, height: 800 } }
    ];

    // Generate 5 sessions with different viewers
    mockViewers.forEach((viewer, viewerIndex) => {
      const sessionId = `demo_session_${viewerIndex + 1}`;
      const sessionStartTime = Date.now() - Math.floor(Math.random() * 7 * 24 * 60 * 60 * 1000); // Random time in last 7 days
      const sessionDuration = Math.floor(Math.random() * 20 * 60 * 1000) + 5 * 60 * 1000; // 5-25 minutes
      
      // Add session start event
      events.push({
        sessionId,
        eventType: 'pdf_preview_opened',
        timestamp: new Date(sessionStartTime).toISOString(),
        timestampMs: sessionStartTime,
        sessionStartTime,
        sessionDurationMs: sessionDuration,
        viewerInfo: viewer,
        userAgent: viewer.deviceInfo,
        viewport: viewer.viewport
      });

      // Generate slide view events
      const numSlides = 15;
      const slidesToView = Array.from({ length: numSlides }, (_, i) => i + 1)
        .sort(() => Math.random() - 0.5)
        .slice(0, Math.floor(Math.random() * numSlides) + 5); // View 5-15 slides

      slidesToView.forEach((slideNum, index) => {
        const slideStartTime = sessionStartTime + (index * 60000) + Math.floor(Math.random() * 30000);
        const timeSpent = Math.floor(Math.random() * 60000) + 30000; // 30-90 seconds per slide

        // Add slide view start
        events.push({
          sessionId,
          eventType: 'slide_view_start',
          timestamp: new Date(slideStartTime).toISOString(),
          timestampMs: slideStartTime,
          sessionStartTime,
          sessionDurationMs: sessionDuration,
          slideNumber: slideNum,
          viewSequence: index + 1,
          isFirstTimeViewing: true,
          viewerInfo: viewer,
          userAgent: viewer.deviceInfo,
          viewport: viewer.viewport
        });

        // Add slide view complete
        events.push({
          sessionId,
          eventType: 'slide_view_complete',
          timestamp: new Date(slideStartTime + timeSpent).toISOString(),
          timestampMs: slideStartTime + timeSpent,
          sessionStartTime,
          sessionDurationMs: sessionDuration,
          slideNumber: slideNum,
          timeSpentMs: timeSpent,
          timeSpentSeconds: timeSpent / 1000,
          viewSequence: index + 1,
          isRevisit: false,
          viewerInfo: viewer,
          userAgent: viewer.deviceInfo,
          viewport: viewer.viewport
        });
      });

      // Add session complete event
      events.push({
        sessionId,
        eventType: 'pdf_preview_session_complete',
        timestamp: new Date(sessionStartTime + sessionDuration).toISOString(),
        timestampMs: sessionStartTime + sessionDuration,
        sessionStartTime,
        sessionDurationMs: sessionDuration,
        totalViewTimeMs: sessionDuration,
        slidesViewed: slidesToView,
        uniqueSlidesViewed: slidesToView.length,
        totalSlideViews: slidesToView.length * 2, // Each slide has start and complete events
        completionRate: Math.round((slidesToView.length / numSlides) * 100),
        viewerInfo: viewer,
        userAgent: viewer.deviceInfo,
        viewport: viewer.viewport
      });
    });

    return events;
  };

  const processAnalyticsData = (events) => {
    const viewers = new Set();
    const slideViews = {};
    const sessionDurations = [];
    const viewerEngagement = {};
    const timeDistribution = {
      '0-2min': 0,
      '2-5min': 0,
      '5-10min': 0,
      '10-15min': 0,
      '15+min': 0
    };

    // Group events by session
    const sessions = {};
    events.forEach(event => {
      const sessionId = event.sessionId;
      if (!sessions[sessionId]) {
        sessions[sessionId] = {
          events: [],
          startTime: event.sessionStartTime,
          viewerInfo: event.viewerInfo,
          duration: event.sessionDurationMs
        };
      }
      sessions[sessionId].events.push(event);
    });

    // Process each session
    Object.values(sessions).forEach(session => {
      // Track unique viewers
      if (session.viewerInfo?.email) {
        viewers.add(session.viewerInfo.email);
      }

      // Track session duration
      const duration = session.duration / 1000 / 60; // Convert to minutes
      sessionDurations.push(duration);
      
      // Categorize session duration
      if (duration < 2) timeDistribution['0-2min']++;
      else if (duration < 5) timeDistribution['2-5min']++;
      else if (duration < 10) timeDistribution['5-10min']++;
      else if (duration < 15) timeDistribution['10-15min']++;
      else timeDistribution['15+min']++;

      // Track viewer engagement
      if (session.viewerInfo?.email) {
        if (!viewerEngagement[session.viewerInfo.email]) {
          viewerEngagement[session.viewerInfo.email] = {
            name: session.viewerInfo.fullName,
            totalTime: 0,
            sessions: 0,
            lastVisit: session.events[0].timestamp,
            deviceInfo: session.events[0].userAgent,
            viewport: session.events[0].viewport
          };
        }
        
        // Update viewer engagement metrics
        viewerEngagement[session.viewerInfo.email].totalTime += session.duration;
        viewerEngagement[session.viewerInfo.email].sessions++;
        
        // Update last visit if this session is more recent
        const currentLastVisit = new Date(viewerEngagement[session.viewerInfo.email].lastVisit);
        const newLastVisit = new Date(session.events[0].timestamp);
        if (newLastVisit > currentLastVisit) {
          viewerEngagement[session.viewerInfo.email].lastVisit = session.events[0].timestamp;
        }
      }

      // Track slide views from session events
      session.events.forEach(event => {
        if (event.eventType === 'slide_view_complete') {
          const slideNum = event.slideNumber;
          if (!slideViews[slideNum]) {
            slideViews[slideNum] = {
              views: 0,
              totalTime: 0
            };
          }
          slideViews[slideNum].views++;
          slideViews[slideNum].totalTime += event.timeSpentMs || 0;
        }
      });
    });

    // Convert slide views to chart data
    const slideViewsData = Object.entries(slideViews).map(([slide, data]) => ({
      slide: `Slide ${slide}`,
      views: data.views,
      avgTime: Math.round(data.totalTime / data.views / 1000) // Convert to seconds
    })).sort((a, b) => parseInt(a.slide.split(' ')[1]) - parseInt(b.slide.split(' ')[1]));

    // Convert time distribution to chart data
    const timeDistributionData = Object.entries(timeDistribution).map(([range, count]) => ({
      range,
      count
    }));

    // Process viewer engagement
    const viewerEngagementData = Object.entries(viewerEngagement)
      .map(([email, data]) => ({
        email,
        name: data.name,
        totalTime: Math.round(data.totalTime / 1000 / 60), // Convert to minutes
        sessions: data.sessions,
        lastVisit: new Date(data.lastVisit).toLocaleDateString(),
        deviceInfo: data.deviceInfo,
        viewport: data.viewport
      }))
      .sort((a, b) => b.totalTime - a.totalTime)
      .slice(0, 5);

    return {
      totalViews: events.length,
      uniqueViewers: viewers.size,
      averageSessionDuration: sessionDurations.length ? 
        Math.round(sessionDurations.reduce((a, b) => a + b, 0) / sessionDurations.length) : 0,
      slideViewsData,
      timeDistributionData,
      viewerEngagementData
    };
  };

  if (!show) return null;

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
        <div className="bg-white rounded-3xl shadow-2xl max-w-5xl w-[90vw] h-[90vh] p-10 relative flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={(e) => {
        // Close if clicking the backdrop
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="bg-white rounded-3xl shadow-2xl max-w-5xl w-[90vw] h-[90vh] relative flex flex-col animate-fade-in overflow-hidden">
        {/* Sticky Header */}
        <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-10 py-6 flex items-center justify-between">
          <h2 className="text-3xl font-bold text-blue-700 flex items-center gap-3">
            <BarChart3 className="w-8 h-8 text-blue-600" />
            Analytics Dashboard
          </h2>
          <button
            className="text-gray-400 hover:text-gray-600 transition-colors"
            onClick={onClose}
            aria-label="Close analytics"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-auto p-10">
          {analyticsData ? (
            <div className="flex flex-col gap-8">
              {/* Top KPI stats grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 text-center border border-blue-200">
                  <div className="text-3xl font-extrabold text-blue-700">{analyticsData.totalViews}</div>
                  <div className="text-sm text-gray-600 mt-1">Total Views</div>
                </div>
                <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl p-6 text-center border border-emerald-200">
                  <div className="text-3xl font-extrabold text-emerald-700">{analyticsData.uniqueViewers}</div>
                  <div className="text-sm text-gray-600 mt-1">Unique Viewers</div>
                </div>
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6 text-center border border-purple-200">
                  <div className="text-3xl font-extrabold text-purple-700">{analyticsData.averageSessionDuration}</div>
                  <div className="text-sm text-gray-600 mt-1">Avg. Session (min)</div>
                </div>
                <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-6 text-center border border-orange-200">
                  <div className="text-3xl font-extrabold text-orange-700">
                    {Math.round(analyticsData.slideViewsData.reduce((acc, curr) => acc + curr.views, 0) / analyticsData.slideViewsData.length)}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">Avg. Views per Slide</div>
                </div>
              </div>

              {/* Slide Views Chart */}
              <div className="bg-white rounded-xl p-6 border">
                <h3 className="text-lg font-semibold text-gray-700 mb-4">Slide Engagement</h3>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={analyticsData.slideViewsData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="slide" />
                      <YAxis yAxisId="left" orientation="left" stroke="#8884d8" />
                      <YAxis yAxisId="right" orientation="right" stroke="#82ca9d" />
                      <Tooltip />
                      <Bar yAxisId="left" dataKey="views" fill="#8884d8" name="Views" />
                      <Bar yAxisId="right" dataKey="avgTime" fill="#82ca9d" name="Avg Time (s)" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Session Duration Distribution */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="bg-white rounded-xl p-6 border">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Session Duration Distribution</h3>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={analyticsData.timeDistributionData}
                          dataKey="count"
                          nameKey="range"
                          cx="50%"
                          cy="50%"
                          outerRadius={100}
                          label
                        >
                          {analyticsData.timeDistributionData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Top Engaged Viewers */}
                <div className="bg-white rounded-xl p-6 border">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Top Engaged Viewers</h3>
                  <div className="space-y-4">
                    {analyticsData.viewerEngagementData.map((viewer, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <div className="font-medium">{viewer.name}</div>
                          <div className="text-xs text-gray-500">{viewer.email}</div>
                          <div className="text-xs text-gray-500 mt-1">
                            {viewer.deviceInfo.includes('Mobile') ? '📱 Mobile' : '💻 Desktop'} • 
                            {viewer.viewport.width}x{viewer.viewport.height}
                          </div>
                        </div>
                        <div className="text-right">
                          {/* <div className="text-sm font-bold text-blue-600">{viewer.totalTime} min</div> */}
                          <div className="text-xs text-gray-500">{viewer.sessions} sessions</div>
                          <div className="text-xs text-gray-500">Last: {viewer.lastVisit}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center min-h-[60vh]">
              <BarChart3 className="w-16 h-16 text-blue-200 mb-6" />
              <div className="text-2xl text-gray-500 text-center mb-2 font-semibold">No analytics data available</div>
              <div className="text-base text-gray-400">Analytics will appear here as viewers interact with your pitch deck.</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnalyticsModal; 